import React, { useEffect, useState, useRef, useMemo } from "react";
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import authConfig from "../../authConfig";
import {
    Container,
    Row,
    Col,
    Table,
    Button,
    Modal,
    Form,
    Spinner,
    Alert,
    Badge
} from "react-bootstrap";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-classic/build/ckeditor";
import VideoUploader from "./VideoUploader";
import VideoPlayer from "./VideoPlayer";
import PdfUploader from "./PdfUploader"; // added for PDF uploads
import SecureFileLink from "../meeting/SecureFileLink"; // use secure link component for PDFs
import { FaEdit, FaVideo, FaRegFilePdf, FaRegTrashAlt, FaToggleOff, FaToggleOn, FaList } from 'react-icons/fa'
import {
    format
} from 'date-fns';
function MemberTrainingAdminPage() {
    const { t } = useTranslation();
    const isAuthenticated = useIsAuthenticated();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // filtering and sorting state
    const [filterType, setFilterType] = useState("All");
    const [filterCategory, setFilterCategory] = useState("All");
    const [sortField, setSortField] = useState("title");
    const [sortDirection, setSortDirection] = useState("asc");
    const [showFilters, setShowFilters] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // validation state for required fields
    const [validationErrors, setValidationErrors] = useState({});

    // Video viewer modal states
    const [showVideoModal, setShowVideoModal] = useState(false);
    // store trainingId and fileId for VideoPlayer component
    const [videoTrainingId, setVideoTrainingId] = useState(null);
    const [videoFileId, setVideoFileId] = useState(null);
    const [videoTitle, setVideoTitle] = useState("");

    // Conversion status states
    const [conversionStatus, setConversionStatus] = useState(null);
    const [isConversionPolling, setIsConversionPolling] = useState(false);
    const conversionPollRef = useRef(null);

    // Completions (admin view)
    const [completions, setCompletions] = useState([]);
    const [loadingCompletions, setLoadingCompletions] = useState(false);
    const [showCompletionsModal, setShowCompletionsModal] = useState(false);
    const [completionsTrainingId, setCompletionsTrainingId] = useState(null);
    const [completionsTrainingTitle, setCompletionsTrainingTitle] = useState("");

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function acquireToken() {
        if (!isAuthenticated) return null;
        try {
            const response = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            return response?.accessToken ?? null;
        } catch (err) {
            console.error("Token acquisition failed", err);
            setError("Unable to acquire access token.");
            return null;
        }
    }

    // now returns the fetched items array so callers can use it immediately
    async function loadData() {
        setError(null);
        setLoading(true);
        const token = await acquireToken();
        try {
            const axiosInstance = axios.create({
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            const resp = await axiosInstance.get(authConfig.api.urls.getMemberTraining);
            const arr = Array.isArray(resp.data) ? resp.data : [];
            setItems(arr);
            return arr;
        } catch (err) {
            console.error(err);
            setError("Error fetching member training: " + (err?.message || "unknown"));
            return [];
        } finally {
            setLoading(false);
        }
    }

    // derive unique type/category lists from items for filter dropdowns
    const typeOptions = useMemo(() => {
        const setTypes = new Set();
        (items || []).forEach(i => { if (i && i.type) setTypes.add(i.type); });
        // include common types to keep consistent ordering
        const base = ["Video", "PDF", "HTML", "ExternalURL"];
        const result = base.concat(Array.from(setTypes).filter(x => !base.includes(x)));
        return ["All", ...result.filter((v, i) => result.indexOf(v) === i)];
    }, [items]);

    const categoryOptions = useMemo(() => {
        const setCats = new Set();
        (items || []).forEach(i => { if (i && i.category) setCats.add(i.category); });
        const arr = Array.from(setCats).sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
        return ["All", ...arr];
    }, [items]);

    // compute displayed items after applying filters and sorting
    const displayedItems = useMemo(() => {
        let arr = Array.isArray(items) ? items.slice() : [];
        if (filterType && filterType !== "All") {
            arr = arr.filter(x => String(x.type || "").toLowerCase() === String(filterType || "").toLowerCase());
        }
        if (filterCategory && filterCategory !== "All") {
            arr = arr.filter(x => String(x.category || "").toLowerCase() === String(filterCategory || "").toLowerCase());
        }

        const field = sortField || 'title';
        arr.sort((a, b) => {
            const va = (a && a[field]) ? String(a[field]) : "";
            const vb = (b && b[field]) ? String(b[field]) : "";
            let cmp = va.localeCompare(vb, undefined, { sensitivity: 'base', numeric: true });
            if (sortDirection === 'desc') cmp = -cmp;
            return cmp;
        });

        return arr;
    }, [items, filterType, filterCategory, sortField, sortDirection]);

    function openAdd() {
        setEditingItem({
            title: "",
            description: "",
            category: "",
            type: "Video",
            contentUrl: "",
            contentFileName: "",
            contentMimeType: "",
            contentHtml: "",
            enabled: true
        });
        setValidationErrors({});
        setShowEditModal(true);
    }

    function openEdit(item) {
        // shallow clone to avoid mutating list state directly
        setEditingItem({ ...item });
        setValidationErrors({});
        setShowEditModal(true);

        // If this is a video with an uploaded file, check conversion status
        if (item && item.type === 'Video' && item.contentFileName && item.id) {
            startPollingConversion(item.id);
        }
    }

    function closeModal() {
        setShowEditModal(false);
        setEditingItem(null);
        setSaving(false);
        setError(null);
        setValidationErrors({});

        // stop any polling
        if (conversionPollRef.current) {
            clearInterval(conversionPollRef.current);
            conversionPollRef.current = null;
        }
        setConversionStatus(null);
        setIsConversionPolling(false);

        // clear completions when leaving edit context
        setCompletions([]);
        setLoadingCompletions(false);
    }

    // Open video popup for viewing
    function openVideo(item) {
        if (!item || !item.contentFileName) {
            setError("No video file available to view.");
            return;
        }

        // The server stores HLS/mp4 under a folder named by the training record id
        // VideoPlayer expects trainingId and fileId (fileId is the folder/name used when uploading)
        const trainingId = item.id;
        const fileId = item.id; // uploads use the training id as the fileId

        setVideoTrainingId(trainingId);
        setVideoFileId(fileId);
        setVideoTitle(item.title || "Training Video");
        setShowVideoModal(true);
    }

    function closeVideoModal() {
        setShowVideoModal(false);
        setVideoTrainingId(null);
        setVideoFileId(null);
        setVideoTitle("");
    }

    function isBlankHtml(html) {
        if (!html) return true;
        // remove tags and whitespace
        const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
        return stripped.length ===0;
    }

    function validateEditingItem() {
        const errors = {};
        if (!editingItem) return false;
        if (!editingItem.title || String(editingItem.title).trim() === "") {
            errors.title = "Title is required.";
        }
        if (!editingItem.category || String(editingItem.category).trim() === "") {
            errors.category = "Category is required.";
        }
        if (!editingItem.description || isBlankHtml(editingItem.description)) {
            errors.description = "Description is required.";
        }

        setValidationErrors(errors);
        if (Object.keys(errors).length >0) {
            setError("Please correct the highlighted fields.");
            return false;
        }
        setError(null);
        return true;
    }

    async function saveItem() {
        setError(null);
        if (!validateEditingItem()) {
            return;
        }
        if (!editingItem || !editingItem.title || editingItem.title.trim() === "") {
            setError("Title is required.");
            return;
        }
        setSaving(true);
        const token = await acquireToken();
        if (!token) {
            setSaving(false);
            return;
        }
        const axiosInstance = axios.create({
            headers: { Authorization: `Bearer ${token}` }
        });

        try {
            if (editingItem.id) {
                // Update existing
                await axiosInstance.put(
                    `${authConfig.api.urls.getMemberTraining}/${editingItem.id}`,
                    editingItem
                );
                await loadData();
                closeModal();
            } else {
                // Create new (server will create Id)
                const resp = await axiosInstance.post(authConfig.api.urls.getMemberTraining, editingItem);
                const created = resp?.data;
                // Refresh list and try to obtain created item
                const newItems = await loadData();

                // If server returned the created item with id, use it; otherwise try to find it by matching unique-ish fields
                if (created && created.id) {
                    setEditingItem(created);
                    setShowEditModal(true); // keep in edit mode so user can upload video / further edit
                } else {
                    const found = newItems.find(x =>
                        x.title === editingItem.title &&
                        x.type === editingItem.type &&
                        (x.category || "") === (editingItem.category || "")
                    );
                    if (found) {
                        setEditingItem(found);
                        setShowEditModal(true);
                    } else {
                        // fallback: close modal if we couldn't determine created item
                        closeModal();
                    }
                }
            }
        } catch (err) {
            console.error(err);
            setError("Save failed: " + (err?.response?.data || err?.message || "unknown"));
        } finally {
            setSaving(false);
        }
    }

    async function deleteItem(item) {
        if (!window.confirm(`Delete "${item.title}"?`)) return;
        setError(null);
        const token = await acquireToken();
        if (!token) return;
        const axiosInstance = axios.create({
            headers: { Authorization: `Bearer ${token}` }
        });
        try {
            await axiosInstance.delete(`${authConfig.api.urls.getMemberTraining}/${item.id}`);
            await loadData();
        } catch (err) {
            console.error(err);
            setError("Delete failed: " + (err?.message || "unknown"));
        }
    }

    async function toggleEnabled(item) {
        setError(null);
        const token = await acquireToken();
        if (!token) return;
        const axiosInstance = axios.create({
            headers: { Authorization: `Bearer ${token}` }
        });
        try {
            const newEnabled = !item.enabled;
            await axiosInstance.patch(
                `${authConfig.api.urls.getMemberTraining}/enable/${item.id}?enabled=${newEnabled}`
            );
            await loadData();
        } catch (err) {
            console.error(err);
            setError("Update enabled failed: " + (err?.message || "unknown"));
        }
    }

    // helper: normalize status value from API to string
    function normalizeStatus(raw) {
        if (raw === null || raw === undefined) return null;
        // handle numeric enum values
        if (typeof raw === 'number') {
            switch (raw) {
                case 0: return 'Queued';
                case 1: return 'Processing';
                case 2: return 'Completed';
                case 3: return 'Failed';
                default: return String(raw);
            }
        }
        // string values
        const s = String(raw);
        if (/queued/i.test(s)) return 'Queued';
        if (/processing/i.test(s)) return 'Processing';
        if (/completed/i.test(s)) return 'Completed';
        if (/failed/i.test(s)) return 'Failed';
        return s;
    }

    async function getConversionStatusOnce(trainingId) {
        setError(null);
        const token = await acquireToken();
        if (!token) return null;
        try {
            const axiosInstance = axios.create({
                headers: { Authorization: `Bearer ${token}` }
            });
            const resp = await axiosInstance.get(
                `${authConfig.api.urls.getMemberTraining}/videoconversionstatus/${trainingId}`
            );
            const data = resp?.data ?? null;
            if (!data) return null;
            const statusName = normalizeStatus(data.status ?? data.Status ?? data.StatusCode ?? null);
            return {
                status: statusName,
                error: data.error || data.Error || null,
                raw: data
            };
        } catch (err) {
            //404 means no conversion known for this training id
            if (err?.response?.status === 404) return null;
            console.error('Failed to get conversion status', err);
            return null;
        }
    }

    function startPollingConversion(trainingId) {
        // stop any existing poll
        if (conversionPollRef.current) {
            clearInterval(conversionPollRef.current);
            conversionPollRef.current = null;
        }

        // immediate check then poll while queued/processing
        (async () => {
            const first = await getConversionStatusOnce(trainingId);
            if (!first) return;
            setConversionStatus(first);
            if (first.status === 'Queued' || first.status === 'Processing') {
                setIsConversionPolling(true);
                conversionPollRef.current = setInterval(async () => {
                    const res = await getConversionStatusOnce(trainingId);
                    if (!res) {
                        // nothing known, stop polling
                        clearInterval(conversionPollRef.current);
                        conversionPollRef.current = null;
                        setIsConversionPolling(false);
                        setConversionStatus(null);
                        return;
                    }
                    setConversionStatus(res);
                    if (res.status === 'Completed' || res.status === 'Failed') {
                        clearInterval(conversionPollRef.current);
                        conversionPollRef.current = null;
                        setIsConversionPolling(false);
                        // refresh list so UI reflects available HLS files
                        await loadData();
                    }
                },5000);
            }
        })();
    }

    // New: handle video uploader callback to update the record with the uploaded filename
    async function handleVideoUploaded(filename) {
        setError(null);

        if (!editingItem || !editingItem.id) {
            setError("Cannot attach uploaded file: training item not saved yet.");
            return;
        }

        setSaving(true);
        const token = await acquireToken();
        if (!token) {
            setSaving(false);
            return;
        }

        const axiosInstance = axios.create({
            headers: { Authorization: `Bearer ${token}` }
        });

        try {
            // update content fields; server PUT expects the model, so send full model with updated fields
            const updated = {
                ...editingItem,
                contentFileName: filename,
                contentMimeType: "video/mp4",
                // Use the API route that serves videos; frontend will call this relative URL
                contentUrl: `/api/membertraining/video/${encodeURIComponent(filename)}`
            };

            await axiosInstance.put(`${authConfig.api.urls.getMemberTraining}/${editingItem.id}`, updated);

            // update local editing state and refresh list
            setEditingItem(updated);
            await loadData();

            // start checking conversion status for this training record
            if (editingItem.id) {
                startPollingConversion(editingItem.id);
            }
        } catch (err) {
            console.error("Failed to update record after upload:", err);
            setError("Updating record with uploaded filename failed: " + (err?.response?.data || err?.message || "unknown"));
        } finally {
            setSaving(false);
        }
    }

    // New: handle PDF uploader callback to update record with uploaded filename
    async function handlePdfUploaded(fileName, url) {
        setError(null);

        if (!editingItem || !editingItem.id) {
            setError("Cannot attach uploaded file: training item not saved yet.");
            return;
        }

        setSaving(true);
        const token = await acquireToken();
        if (!token) {
            setSaving(false);
            return;
        }

        const axiosInstance = axios.create({
            headers: { Authorization: `Bearer ${token}` }
        });

        try {
            const updated = {
                ...editingItem,
                contentFileName: fileName,
                contentMimeType: "application/pdf",
                contentUrl: url || `/api/membertraining/pdf/${encodeURIComponent(fileName)}`
            };

            await axiosInstance.put(`${authConfig.api.urls.getMemberTraining}/${editingItem.id}`, updated);

            setEditingItem(updated);
            await loadData();
        } catch (err) {
            console.error("Failed to update record after PDF upload:", err);
            setError("Updating record with uploaded PDF failed: " + (err?.response?.data || err?.message || "unknown"));
        } finally {
            setSaving(false);
        }
    }

    async function loadCompletions(trainingId) {
        setLoadingCompletions(true);
        setCompletions([]);
        setError(null);
        const token = await acquireToken();
        if (!token) {
            setLoadingCompletions(false);
            return;
        }
        try {
            const axiosInstance = axios.create({ headers: { Authorization: `Bearer ${token}` } });
            const resp = await axiosInstance.get(`${authConfig.api.urls.getMemberTraining}/completions/${encodeURIComponent(trainingId)}`);
            const arr = Array.isArray(resp.data) ? resp.data : [];
            setCompletions(arr);
        } catch (err) {
            console.error('Failed to load completions', err);
            setError('Failed to load completions: ' + (err?.message || 'unknown'));
            setCompletions([]);
        } finally {
            setLoadingCompletions(false);
        }
    }

    function openCompletions(item) {
        if (!item || !item.id) return;
        setCompletionsTrainingId(item.id);
        setCompletionsTrainingTitle(item.title || '');
        setShowCompletionsModal(true);
        loadCompletions(item.id);
    }

    function closeCompletionsModal() {
        setShowCompletionsModal(false);
        setCompletionsTrainingId(null);
        setCompletionsTrainingTitle("");
        setCompletions([]);
    }

    function onFieldChange(e) {
        const { name, value, type, checked } = e.target;
        setEditingItem(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
        // clear validation error for field as user types
        setValidationErrors(prev => ({ ...prev, [name]: null }));
    }

    return (
        <Container fluid>
            <Row className="mb-3 align-items-center">
                <Col><h5>Member Training</h5></Col>
                <Col className="text-end">
                    <Button variant={showFilters ? "outline-primary" : "outline-secondary"} className="me-2" onClick={() => setShowFilters(s => !s)} aria-expanded={showFilters}>
                        {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>
                    <Button variant="primary" onClick={openAdd}>Add New</Button>
                </Col>
            </Row>

            {/* Filters and sorting controls */}
            {showFilters && (
                <Row className="mb-3 g-2">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small">Filter by Type</Form.Label>
                            <Form.Select value={filterType} onChange={e => setFilterType(e.target.value)}>
                                {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small">Filter by Category</Form.Label>
                            <Form.Select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                                {categoryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small">Sort Field</Form.Label>
                            <Form.Select value={sortField} onChange={e => setSortField(e.target.value)}>
                                <option value="title">Title</option>
                                <option value="type">Type</option>
                                <option value="category">Category</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small">Direction</Form.Label>
                            <Form.Select value={sortDirection} onChange={e => setSortDirection(e.target.value)}>
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : (
                <Table bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Type</th>
                            <th>Category</th>
                            <th>Enabled</th>
                            <th style={{ width:300 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedItems.length ===0 && (
                            <tr>
                                <td colSpan="5" className="text-center text-muted">No training found</td>
                            </tr>
                        )}
                        {displayedItems.map(it => (
                            <tr key={it.id}>
                                <td>{it.title}</td>
                                <td>{it.type}</td>
                                <td>{it.category}</td>
                                <td>
                                    {it.enabled ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}
                                </td>
                                <td>
                                    <Button size="sm" variant="outline-primary" onClick={() => openEdit(it)} className="me-2"><FaEdit title="Edit"/></Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => deleteItem(it)} className="me-2"><FaRegTrashAlt title="Delete"/></Button>
                                    <Button size="sm" variant="outline-secondary" onClick={() => toggleEnabled(it)}>
                                        {it.enabled ? <FaToggleOff title="Disable" /> : <FaToggleOn title="Enable"/>}
                                    </Button>

                                    {/* Show "View" button when there is a video file available */}
                                    {it.type === "Video" && it.contentFileName && (
                                        <Button size="sm" variant="outline-info" className="ms-2" onClick={() => openVideo(it)}>
                                            <FaVideo title="Play Video"/>
                                        </Button>
                                    )}
                                    {/* Show PDF link when PDF present */}
                                    {it.type === "PDF" && it.contentFileName && (
                                        <SecureFileLink
                                            type="button"
                                            variant="outline-info"
                                            className="ms-2"
                                            allowdownload={false}
                                            url={`${authConfig.api.urls.getMemberTrainingPdf}/${encodeURIComponent(it.id)}.pdf`}
                                            filename={it.contentFileName}>
                                            <FaRegFilePdf title="Open Pdf"/>
                                        </SecureFileLink>
                                    )}

                                    {/* Open completions in separate view */}
                                    <Button size="sm" variant="outline-info" className="ms-2" onClick={() => openCompletions(it)}>
                                        <FaList title="Show Completions"/>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showEditModal} onHide={closeModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingItem && editingItem.id ? "Edit Training" : "Add Training"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {/* Show conversion status alert when present */}
                    {conversionStatus && (conversionStatus.status === 'Queued' || conversionStatus.status === 'Processing') && (
                        <Alert variant="info">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Video conversion in progress: {conversionStatus.status}. It may take a few minutes; the page will refresh when complete.
                        </Alert>
                    )}
                    {conversionStatus && conversionStatus.status === 'Failed' && (
                        <Alert variant="danger">Video conversion failed: {conversionStatus.error || 'See server logs.'}</Alert>
                    )}

                    {editingItem ? (
                        <Form>
                            <Form.Group className="mb-2" controlId="mtTitle">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    name="title"
                                    value={editingItem.title || ""}
                                    onChange={onFieldChange}
                                    isInvalid={!!validationErrors.title}
                                />
                                <Form.Control.Feedback type="invalid">{validationErrors.title}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-2" controlId="mtDescription">
                                <Form.Label>Description</Form.Label>
                                <CKEditor
                                    editor={Editor}
                                    data={editingItem.description || ""}
                                    onChange={(event, editor) => {
                                        const html = editor.getData();
                                        setEditingItem(prev => ({ ...prev, description: html }));
                                        setValidationErrors(prev => ({ ...prev, description: null }));
                                    }}
                                />
                                {validationErrors.description && (
                                    <div className="invalid-feedback d-block">{validationErrors.description}</div>
                                )}
                                <Form.Text className="text-muted">Short description shown in lists.</Form.Text>
                            </Form.Group>

                            <Row className="g-2">
                                <Col md={6}>
                                    <Form.Group className="mb-2" controlId="mtCategory">
                                        <Form.Label>Category</Form.Label>
                                        <Form.Control
                                            name="category"
                                            value={editingItem.category || ""}
                                            onChange={onFieldChange}
                                            isInvalid={!!validationErrors.category}
                                        />
                                        <Form.Control.Feedback type="invalid">{validationErrors.category}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-2" controlId="mtType">
                                        <Form.Label>Type</Form.Label>
                                        <Form.Select name="type" value={editingItem.type || "Video"} onChange={onFieldChange}>
                                            <option>Video</option>
                                            <option>PDF</option>
                                            <option>HTML</option>
                                            <option>ExternalURL</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {editingItem.type === "ExternalURL" && (
                                <Form.Group className="mb-2" controlId="mtContentUrl">
                                    <Form.Label>Content URL</Form.Label>
                                    <Form.Control
                                        name="contentUrl"
                                        value={editingItem.contentUrl || ""}
                                        onChange={onFieldChange}
                                        placeholder="http(s)://..."
                                    />
                                    <Form.Text className="text-muted">Use for external links or file endpoints.</Form.Text>
                                </Form.Group>
                            )}

                            {editingItem.type === "HTML" && (
                                <Form.Group className="mb-2" controlId="mtContentHtml">
                                    <Form.Label>Content HTML</Form.Label>
                                    <CKEditor
                                        editor={Editor}
                                        data={editingItem.contentHtml || ""}
                                        onChange={(event, editor) => {
                                            const html = editor.getData();
                                            setEditingItem(prev => ({ ...prev, contentHtml: html }));
                                        }}
                                    />
                                    
                                </Form.Group>
                            )}

                            {editingItem.type === "Video" && editingItem.id && (
                                <Form.Group className="mb-2" controlId="mtVideoUploader">
                                    <Form.Label>Upload / replace video</Form.Label>
                                    <VideoUploader fileId={editingItem.id} onUpload={handleVideoUploaded} />
                                    {editingItem.contentFileName && (
                                        <div className="mt-2 text-muted">
                                            Current file: <code>{editingItem.contentFileName}</code>
                                            <Button
                                                size="sm"
                                                className="ms-2"
                                                variant="outline-primary"
                                                onClick={() => openVideo(editingItem)}>
                                                View
                                            </Button>
                                        </div>
                                    )}
                                </Form.Group>
                            )}

                            {editingItem.type === "PDF" && editingItem.id && (
                                <Form.Group className="mb-2" controlId="mtPdfUploader">
                                    <Form.Label>Upload / replace PDF</Form.Label>
                                    <PdfUploader trainingId={editingItem.id} onUpload={handlePdfUploaded} />
                                    {editingItem.contentFileName && (
                                        <div className="mt-2 text-muted">
                                            Current file: <code>{editingItem.contentFileName}</code>
                                            <SecureFileLink
                                                type="button"
                                                className="ms-2"
                                                variant="outline-primary"
                                                url={`${authConfig.api.urls.getMemberTrainingPdf}/${encodeURIComponent(editingItem.id)}.pdf`}
                                                filename={editingItem.contentFileName}>
                                                Open Pdf
                                            </SecureFileLink>
                                        </div>
                                    )}
                                </Form.Group>
                            )}

                            <Form.Group className="mb-2" controlId="mtEnabled">
                                <Form.Check
                                    type="checkbox"
                                    label="Enabled"
                                    name="enabled"
                                    checked={!!editingItem.enabled}
                                    onChange={onFieldChange}
                                />
                            </Form.Group>

                        </Form>
                    ) : (
                        <div className="text-center"><Spinner animation="border" /></div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {editingItem && editingItem.id && (
                        <Button variant="outline-info" onClick={() => editingItem && openCompletions(editingItem)} disabled={loadingCompletions} className="ms-2">
                            {loadingCompletions ? <Spinner animation="border" size="sm" /> : 'View Completions'}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
                    <Button variant="primary" onClick={saveItem} disabled={saving}>
                        {saving ? <><Spinner animation="border" size="sm" /> Saving...</> : "Save"}
                    </Button>
                    {/* Provide quick access to completions from edit modal */}
                </Modal.Footer>
            </Modal>

            {/* Completions modal - separate view */}
            <Modal show={showCompletionsModal} onHide={closeCompletionsModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Completions for {completionsTrainingTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <div className="mb-2 d-flex align-items-center">
                        <div className="ms-auto">
                            <Button size="sm" variant="outline-secondary" onClick={() => completionsTrainingId && loadCompletions(completionsTrainingId)} disabled={loadingCompletions}>
                                {loadingCompletions ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                            </Button>
                        </div>
                    </div>

                    {loadingCompletions ? (
                        <div className="text-center"><Spinner animation="border" size="sm" /></div>
                    ) : (
                        <div>
                            {completions.length ===0 ? (
                                <div className="text-muted">No completions recorded for this training.</div>
                            ) : (
                                <Table size="sm" bordered>
                                    <thead>
                                        <tr>
                                            <th>Member</th>
                                            <th>Completion Date </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {completions.map(c => (
                                            <tr key={c.id}>
                                                <td>{c.memberName || c.MemberName || ''}</td>
                                                <td>{c.completionDate ? format(new Date(c.completionDate),'dd/MM/yyyy HH:mm:ss') :
                                                    (c.CompletionDate ? format(new Date(c.CompletionDate), 'dd/MM/yyyy HH:mm:ss') : '')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeCompletionsModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Video viewer modal */}
            <Modal show={showVideoModal} onHide={closeVideoModal} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{videoTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {videoTrainingId && videoFileId ? (
                        <VideoPlayer trainingId={videoTrainingId} fileId={videoFileId} />
                    ) : (
                        <div className="text-center text-muted">No video selected.</div>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
}

export default MemberTrainingAdminPage;